import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ArrowLeftRight, RotateCcw,
  CheckCircle, AlertCircle, Search, Loader2, RefreshCw,
  Calendar, ChevronDown, X
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { fetchApiDirect, authHelpers, floorsApi } from '../services/api';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

// ─── Helpers ─────────────────────────────────────────────────────────────────
const statusConfig = {
  AVAILABLE: { bg: '#E1F5EE', text: '#0F6E56', label: 'Available' },
  BUSY:      { bg: '#FAEEDA', text: '#854F0B', label: 'Busy'      },
  AWAY:      { bg: '#FAEEDA', text: '#854F0B', label: 'Away'      },
  OFFLINE:   { bg: '#F1EFE8', text: '#5F5E5A', label: 'Offline'   },
};
const avatarColors = [
  { bg: '#9FE1CB', text: '#085041' },
  { bg: '#FAC775', text: '#633806' },
  { bg: '#F4C0D1', text: '#72243E' },
  { bg: '#B5D4F4', text: '#0C447C' },
  { bg: '#C0DD97', text: '#27500A' },
];
const getInitials = (name = '') =>
  name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();

// ─── Component ───────────────────────────────────────────────────────────────
export default function ManualAssignPanel() {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';
  const tc = currentTheme.color;

  const currentUser            = authHelpers.getCurrentUser();
  const requestedByEmployeeId  = currentUser?.id || 7;

  // ── State ────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('assign'); // 'assign' | 'release'

  // Floors
  const [floors, setFloors]             = useState([]);
  // Orders floor filter (separate from verifier floor filter)
  const [ordersFloor, setOrdersFloor]   = useState(null);

  // Verifiers
  const [verifiers, setVerifiers]       = useState([]);
  const [verifiersLoading, setVerifiersLoading] = useState(false);
  const [selectedFloor, setSelectedFloor]       = useState(null);
  const [verifierSearch, setVerifierSearch]     = useState('');
  const [selectedVerifierId, setSelectedVerifierId] = useState(null);

  // Orders
  const [orders, setOrders]             = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [totalItems, setTotalItems]     = useState(0);
  const [currentPage, setCurrentPage]   = useState(1);
  const [orderSearch, setOrderSearch]   = useState('');
  // ✅ multiple orders select
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Date States & Refs
  const [datePreset, setDatePreset]             = useState('today'); // Defaulting to 'today' as in the screenshot
  const [startDate, setStartDate]               = useState('');
  const [endDate, setEndDate]                   = useState('');
  const [isDatePopoverOpen, setIsDatePopoverOpen] = useState(false);
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);
  const [tempStartDate, setTempStartDate]       = useState('');
  const [tempEndDate, setTempEndDate]           = useState('');
  const datePopoverRef                          = useRef(null);

  // Sync temp dates when startDate or endDate change
  useEffect(() => {
    setTempStartDate(startDate || '');
    setTempEndDate(endDate || '');
  }, [startDate, endDate]);

  // Click outside date popover
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePopoverRef.current && !datePopoverRef.current.contains(event.target)) {
        setIsDatePopoverOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const resolveDatePreset = (preset, customStart = '', customEnd = '') => {
    const now = new Date();
    const fmt  = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const fmtS = (d) => { d.setHours(0, 0, 0, 0); return fmt(d); };
    const fmtE = (d) => { d.setHours(23, 59, 59, 999); return fmt(d); };
    switch (preset) {
      case 'today':      return { startDate: fmtS(new Date(now)), endDate: fmtE(new Date(now)) };
      case 'yesterday':  { const y = new Date(now); y.setDate(y.getDate() - 1); return { startDate: fmtS(y), endDate: fmtE(new Date(y)) }; }
      case 'this_week':  { const d = now.getDay(), diff = now.getDate() - d + (d === 0 ? -6 : 1); const s = new Date(now); s.setDate(diff); return { startDate: fmtS(s), endDate: fmtE(new Date(now)) }; }
      case 'this_month': return { startDate: fmtS(new Date(now.getFullYear(), now.getMonth(), 1)), endDate: fmtE(new Date(now.getFullYear(), now.getMonth() + 1, 0)) };
      case 'this_year':  return { startDate: fmtS(new Date(now.getFullYear(), 0, 1)), endDate: fmtE(new Date(now.getFullYear(), 11, 31)) };
      case 'custom':     return { startDate: customStart || '', endDate: customEnd || '' };
      default:           return { startDate: '', endDate: '' };
    }
  };

  const getDateRangeLabel = () => {
    const formatDateStr = (dateStr) => {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return '';
      return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
    };
    if (datePreset === 'custom') {
      if (startDate && endDate)
        return `${formatDateStr(startDate)} - ${formatDateStr(endDate)}`;
      if (startDate) return `From ${formatDateStr(startDate)}`;
      if (endDate) return `To ${formatDateStr(endDate)}`;
      return 'Custom Range';
    }
    const presets = {
      '': 'All View', 'today': 'Today', 'yesterday': 'Yesterday',
      'this_week': 'This Week', 'this_month': 'This Month', 'this_year': 'This Year',
    };
    return presets[datePreset] || 'All View';
  };

  // Assign
  const [assigning, setAssigning] = useState(false);

  // Release
  const [releaseOrderId, setReleaseOrderId] = useState('');
  const [releasing, setReleasing]           = useState(false);

  // Toast
  const [toast, setToast] = useState(null);

  // ── Toast ─────────────────────────────────────────────────────────────────
  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Fetch floors ──────────────────────────────────────────────────────────
  const fetchFloors = useCallback(async () => {
    try {
      const data = await floorsApi.getAllFloors();
      const list = Array.isArray(data) ? data : [];
      setFloors([
        { id: null, label: 'All' },
        ...list.map(f => ({ id: f.id, label: f.name || f.floorName || `Floor ${f.id}` })),
      ]);
    } catch {
      setFloors([{ id: null, label: 'All' }]);
    }
  }, []);

  // ── Fetch verifiers ───────────────────────────────────────────────────────
  const fetchVerifiers = useCallback(async (floorId = null) => {
    setVerifiersLoading(true);
    try {
      const endpoint = floorId
        ? `/manual-assign/available-verifiers?floorId=${floorId}`
        : `/manual-assign/available-verifiers`;
      const data = await fetchApiDirect(endpoint);
      const list =
        Array.isArray(data)                 ? data :
        Array.isArray(data?.verifiers)      ? data.verifiers :
        Array.isArray(data?.data)           ? data.data :
        Array.isArray(data?.content)        ? data.content :
        Array.isArray(data?.employees)      ? data.employees : [];
      setVerifiers(list);
    } catch {
      showToast('error', 'Failed to load verifiers');
      setVerifiers([]);
    } finally {
      setVerifiersLoading(false);
    }
  }, []);

  // ── Fetch PROCESSING orders ───────────────────────────────────────────────
  // All floors:  /manual-assign/floor-orders?status=PROCESSING&page=0&size=10
  // One floor:   /manual-assign/floor-orders/2?status=PROCESSING&page=0&size=10
  const fetchOrders = useCallback(async ({ page = 0, search = '', size = null, floorId = ordersFloor } = {}) => {
    setOrdersLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('status', 'PROCESSING');
      params.append('page', page);
      params.append('size', size ?? itemsPerPage);
      if (search?.trim()) params.append('searchTerm', search.trim());

      const resolved = datePreset
        ? resolveDatePreset(datePreset, startDate, endDate)
        : { startDate: startDate || '', endDate: endDate || '' };

      if (resolved.startDate) params.append('startDate', resolved.startDate);
      if (resolved.endDate)   params.append('endDate',   resolved.endDate);
      if (resolved.startDate || resolved.endDate) params.append('dateType', 'createdDate');

      const endpoint = floorId
        ? `/manual-assign/floor-orders/${floorId}?${params.toString()}`
        : `/manual-assign/processing-orders/all-floors?${params.toString()}`;

      const data = await fetchApiDirect(endpoint);
      const list = floorId
        ? (Array.isArray(data?.orders) ? data.orders : [])
        : (Array.isArray(data?.floors)
            ? data.floors.flatMap(f => f.orders || [])   // sab floors ke orders merge karo
            : []);

      let filteredList = list;
      if (resolved.startDate || resolved.endDate) {
        const startStr = resolved.startDate; // YYYY-MM-DD
        const endStr = resolved.endDate;     // YYYY-MM-DD

        filteredList = list.filter(order => {
          const dateStr = order.orderDate || order.createdAt || order.date || order.dateTime || order.markedAt;
          if (!dateStr) return true; // keep if no date field exists

          let orderYYYYMMDD = '';
          try {
            const d = new Date(dateStr);
            if (!isNaN(d.getTime())) {
              orderYYYYMMDD = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            }
          } catch (e) {
            console.error('Error parsing order date:', e);
          }

          if (!orderYYYYMMDD) return true; // keep if parsing fails

          if (startStr && orderYYYYMMDD < startStr) return false;
          if (endStr && orderYYYYMMDD > endStr) return false;
          return true;
        });
      }

      const total = floorId
        ? (data?.totalCount ?? 0)
        : (data?.totalProcessing ?? 0);
      setOrders(filteredList);
      setTotalItems(resolved.startDate || resolved.endDate ? filteredList.length : total);
    } catch (err) {
      console.error('Orders fetch error:', err);
      setOrders([]);
      setTotalItems(0);
    } finally {
      setOrdersLoading(false);
    }
  }, [ordersFloor, itemsPerPage, datePreset, startDate, endDate]);

  // ── Effects ───────────────────────────────────────────────────────────────
  useEffect(() => { fetchFloors(); }, []);
  useEffect(() => { fetchVerifiers(selectedFloor); }, [selectedFloor]);
  useEffect(() => {
    if (activeTab === 'assign') fetchOrders({ page: 0, search: orderSearch });
  }, [activeTab]);

  // Refetch orders when floor filter changes
  useEffect(() => {
    if (activeTab === 'assign') {
      setCurrentPage(1);
      setSelectedOrderIds([]);
      fetchOrders({ page: 0, search: orderSearch, floorId: ordersFloor });
    }
  }, [ordersFloor]);

  // Refetch orders when date filter changes
  useEffect(() => {
    if (activeTab === 'assign') {
      setCurrentPage(1);
      setSelectedOrderIds([]);
      fetchOrders({ page: 0, search: orderSearch });
    }
  }, [datePreset, startDate, endDate]);

  // debounced order search
  useEffect(() => {
    const t = setTimeout(() => {
      fetchOrders({ page: 0, search: orderSearch });
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [orderSearch]);

  // ── Select helpers ────────────────────────────────────────────────────────
  const toggleOrder = (id) =>
    setSelectedOrderIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );

  const toggleSelectAll = () =>
    setSelectedOrderIds(
      selectedOrderIds.length === orders.length ? [] : orders.map(o => o.id)
    );

  const allSelected = orders.length > 0 && selectedOrderIds.length === orders.length;
  const someSelected = selectedOrderIds.length > 0 && !allSelected;

  // ── Assign (bulk — multiple orders, one verifier) ─────────────────────────
  const handleAssign = async () => {
    if (selectedOrderIds.length === 0) { showToast('error', 'Select at least one order'); return; }
    if (!selectedVerifierId)           { showToast('error', 'Select a verifier'); return; }
    setAssigning(true);
    let successCount = 0;
    let lastError = null;
    try {
      // har selected order ke liye ek call
      for (const numericId of selectedOrderIds) {
        try {
          const data = await fetchApiDirect('/manual-assign/order', {
            method: 'POST',
            body: JSON.stringify({ orderId: numericId, verifierId: selectedVerifierId, requestedByEmployeeId }),
          });
          if (data?.success) successCount++;
          else lastError = data?.message;
        } catch (e) {
          lastError = e?.message;
        }
      }
      if (successCount > 0) {
        const vName = verifiers.find(v => v.id === selectedVerifierId)?.name || 'Verifier';
        showToast('success', `${successCount} order(s) successfully assigned to ${vName}`);
        setSelectedOrderIds([]);
        setSelectedVerifierId(null);
        fetchOrders({ page: currentPage - 1, search: orderSearch });
      } else {
        showToast('error', lastError || 'Assignment failed');
      }
    } finally {
      setAssigning(false);
    }
  };

  // ── Release ───────────────────────────────────────────────────────────────
  const handleRelease = async () => {
    if (!releaseOrderId) { showToast('error', 'Enter Order ID'); return; }
    setReleasing(true);
    try {
      const data = await fetchApiDirect(`/manual-assign/release-flag/${releaseOrderId}`, { method: 'POST' });
      if (data?.success !== false) {
        showToast('success', `Order #${releaseOrderId}  released back to pool`);
        setReleaseOrderId('');
      } else {
        showToast('error', data?.message || 'Release failed');
      }
    } catch (err) {
      showToast('error', err?.message || 'API error');
    } finally {
      setReleasing(false);
    }
  };

  // ── Pagination ────────────────────────────────────────────────────────────
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const handlePageChange = (p) => {
    setCurrentPage(p);
    fetchOrders({ page: p - 1, search: orderSearch });
    setSelectedOrderIds([]);
  };

  // ── Filtered verifiers ────────────────────────────────────────────────────
  const filteredVerifiers = verifiers.filter(v =>
    !verifierSearch ||
    v.name?.toLowerCase().includes(verifierSearch.toLowerCase()) ||
    v.employeeId?.toLowerCase().includes(verifierSearch.toLowerCase())
  );

  // ── Styles ────────────────────────────────────────────────────────────────
  const cardCls = `rounded-2xl border transition-all ${
    isDark ? 'bg-[#111827] border-white/10' : 'bg-white border-gray-200'
  }`;
  const inputCls = `w-full px-3 py-2 text-sm rounded-xl border transition-all outline-none ${
    isDark
      ? 'bg-white/5 border-white/10 text-white placeholder-gray-500 focus:border-white/30'
      : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300'
  }`;
  const labelCls = `block text-[10px] font-black uppercase tracking-widest mb-1.5 ${
    isDark ? 'text-gray-400' : 'text-gray-500'
  }`;

  const CheckBox = ({ checked, indeterminate = false, onClick }) => (
    <div
      onClick={onClick}
      className="w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 cursor-pointer transition-all"
      style={{
        backgroundColor: checked || indeterminate ? tc : 'transparent',
        borderColor: checked || indeterminate ? tc : (isDark ? 'rgba(255,255,255,0.2)' : '#D1D5DB'),
      }}
    >
      {indeterminate && !checked
        ? <div className="w-2 h-0.5 bg-white rounded" />
        : checked
        ? <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        : null
      }
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6 p-6 min-h-screen transition-colors duration-300"
      style={{ backgroundColor: tc + '0D' }}>

      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className={`text-2xl font-black uppercase tracking-widest ${isDark ? 'text-white' : 'text-gray-800'}`}>
          Manual Assign
        </h2>
        <button
          onClick={() => { fetchVerifiers(selectedFloor); fetchOrders({ page: currentPage - 1, search: orderSearch }); }}
          className="p-2 rounded-lg border transition-colors"
          style={{ borderColor: tc + '40', color: tc }}
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Tabs — sirf 2 */}
      <div className={`flex items-center space-x-1 border-b ${isDark ? 'border-white/5' : 'border-gray-200'}`}>
        {[
          { id: 'assign',  label: 'Assign Orders', icon: ArrowLeftRight },
          { id: 'release', label: 'Release',        icon: RotateCcw      },
        ].map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-5 py-2.5 text-[10px] font-black uppercase tracking-widest transition-all relative ${
                isActive ? '' : isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-700'
              }`}
              style={{ color: isActive ? tc : undefined }}
            >
              <Icon className="w-3.5 h-3.5" />
              <span>{tab.label}</span>
              {isActive && <div className="absolute bottom-0 left-0 w-full h-0.5 rounded-t-full" style={{ backgroundColor: tc }} />}
            </button>
          );
        })}
      </div>

      {/* ══════════════════════════════════════════════════════
          ASSIGN ORDERS TAB (bulk select + one verifier)
      ══════════════════════════════════════════════════════ */}
      {activeTab === 'assign' && (
        <div className="flex flex-col lg:flex-row gap-6">

          {/* ── Orders table ── */}
          <div className="flex-1 min-w-0">
            <div className={cardCls}>

              {/* Table header */}
              <div className={`flex items-center justify-between px-5 py-3 border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <p className={`text-xs font-black uppercase tracking-widest ${isDark ? 'text-white' : 'text-gray-800'}`}>
                    Processing Orders
                  </p>
                  {totalItems > 0 && (
                    <span className="px-2 py-0.5 rounded-full text-[9px] font-black"
                      style={{ backgroundColor: tc + '1A', color: tc }}>
                      {totalItems}
                    </span>
                  )}
                  {selectedOrderIds.length > 0 && (
                    <span className="px-2 py-0.5 rounded-full text-[9px] font-black text-white"
                      style={{ backgroundColor: tc }}>
                      {selectedOrderIds.length} selected
                    </span>
                  )}
                  {/* Floor filter tabs for orders */}
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {floors.map(f => (
                      <button key={String(f.id)}
                        onClick={() => setOrdersFloor(f.id)}
                        className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest transition-all"
                        style={{
                          backgroundColor: ordersFloor === f.id ? tc : (isDark ? 'rgba(255,255,255,0.05)' : '#F3F4F6'),
                          color: ordersFloor === f.id ? '#fff' : (isDark ? '#9CA3AF' : '#6B7280'),
                        }}>
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* Date Range Popover */}
                  <div className="relative flex-shrink-0" ref={datePopoverRef}>
                    <button
                      onClick={() => setIsDatePopoverOpen(!isDatePopoverOpen)}
                      className="flex items-center justify-between border rounded-full px-4 py-1.5 shadow-sm cursor-pointer transition-all hover:bg-gray-50/10 h-[38px] text-[13px] font-bold"
                      style={{
                        borderColor: tc,
                        color: tc,
                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.02)' : '#fff',
                      }}
                    >
                      <div className="flex items-center space-x-1.5">
                        <Calendar className="w-4 h-4 text-orange-400" />
                        <span>{getDateRangeLabel()}</span>
                      </div>
                      <ChevronDown className={`w-3.5 h-3.5 ml-2 transition-transform ${isDatePopoverOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isDatePopoverOpen && (
                      <div className={`absolute right-0 mt-2 rounded-2xl shadow-xl border p-2 z-[100] w-48 ${isDark ? 'bg-[#111827] border-white/10' : 'bg-white border-gray-100'}`}>
                        <div className="flex flex-col space-y-0.5">
                          {[
                            { label: 'All View', value: '' },
                            { label: 'Today', value: 'today' },
                            { label: 'Yesterday', value: 'yesterday' },
                            { label: 'This Week', value: 'this_week' },
                            { label: 'This Month', value: 'this_month' },
                            { label: 'This Year', value: 'this_year' },
                            { label: 'Custom Range', value: 'custom' }
                          ].map(opt => {
                            const isSelected = datePreset === opt.value;
                            return (
                              <button
                                key={opt.value}
                                onClick={() => {
                                  if (opt.value === 'custom') {
                                    setIsDatePopoverOpen(false);
                                    setShowCustomDateModal(true);
                                  } else {
                                    setDatePreset(opt.value);
                                    setStartDate('');
                                    setEndDate('');
                                    setCurrentPage(1);
                                    setSelectedOrderIds([]);
                                    setIsDatePopoverOpen(false);
                                  }
                                }}
                                className="w-full px-4 py-2.5 text-left text-xs transition-colors rounded-xl font-medium"
                                style={{
                                  backgroundColor: isSelected ? `${tc}15` : 'transparent',
                                  color: isSelected ? tc : (isDark ? '#D1D5DB' : '#374151'),
                                  fontWeight: isSelected ? 'bold' : 'normal'
                                }}
                              >
                                {opt.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Size dropdown */}
                  <select
                    value={itemsPerPage}
                    onChange={e => {
                      const raw = e.target.value;
                      const val = raw === '99999' ? 99999 : parseInt(raw);
                      setItemsPerPage(val);
                      setCurrentPage(1);
                      setSelectedOrderIds([]);
                      fetchOrders({ page: 0, search: orderSearch, size: val, floorId: ordersFloor });
                    }}
                    className={`text-xs font-black uppercase tracking-widest px-2 py-2 rounded-xl border outline-none cursor-pointer transition-all ${
                      isDark
                        ? 'bg-white/5 border-white/10 text-gray-300'
                        : 'bg-gray-50 border-gray-200 text-gray-700'
                    }`}
                    style={{ minWidth: 72 }}
                  >
                    {[10, 25, 50, 100].map(n => (
                      <option key={n} value={n}>{n} / page</option>
                    ))}
                    <option value={99999}>All</option>
                  </select>
                  {/* Search */}
                  <div className="relative w-48">
                    <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                    <input className={`${inputCls} pl-8 text-xs`} placeholder="Order ID / Name search"
                      value={orderSearch} onChange={e => setOrderSearch(e.target.value)} />
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: 460 }}>
                {ordersLoading ? (
                  <div className="flex items-center justify-center py-14">
                    <Loader2 className="w-6 h-6 animate-spin" style={{ color: tc }} />
                  </div>
                ) : orders.length === 0 ? (
                  <div className="text-center py-14">
                    <p className={`text-sm font-black uppercase tracking-widest ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      No processing orders found
                    </p>
                  </div>
                ) : (
                  <table className="w-full min-w-[560px]">
                    <thead className="sticky top-0 z-10 text-white" style={{ background: tc }}>
                      <tr>
                        <th className="px-4 py-3 w-12">
                          <CheckBox
                            checked={allSelected}
                            indeterminate={someSelected}
                            onClick={toggleSelectAll}
                          />
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Order ID</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Customer</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Amount</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Assigned To</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${isDark ? 'divide-white/5' : 'divide-gray-100'}`}>
                      {orders.map(order => {
                        const isSel = selectedOrderIds.includes(order.id);
                        return (
                          <tr key={order.id}
                            onClick={() => toggleOrder(order.id)}
                            className="cursor-pointer transition-colors"
                            style={{ backgroundColor: isSel ? tc + '1A' : undefined }}
                          >
                            <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                              <CheckBox checked={isSel} onClick={() => toggleOrder(order.id)} />
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`text-sm font-black ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                                {order.orderId || order.id}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`text-sm font-black ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                {order.customerName || 'N/A'}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`text-sm font-black ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                ₹{order.finalAmount ?? order.totalAmount ?? order.netAmount ?? order.amount ?? '—'}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`text-xs font-black px-2 py-1 rounded-full ${isDark ? 'bg-white/5 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                                {order.assignedTo || order.verifierName || 'Unassigned'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className={`flex items-center justify-between px-5 py-3 border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                  <span className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Page {currentPage} / {totalPages}
                  </span>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => i + 1).map(p => (
                      <button key={p} onClick={() => handlePageChange(p)}
                        className="w-7 h-7 rounded-lg text-[10px] font-black transition-all"
                        style={{
                          backgroundColor: currentPage === p ? tc : (isDark ? 'rgba(255,255,255,0.05)' : '#F3F4F6'),
                          color: currentPage === p ? '#fff' : (isDark ? '#9CA3AF' : '#6B7280'),
                        }}>
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Right panel — Verifier select ── */}
          <div className="w-full lg:w-80 flex-shrink-0">
            <div className={`${cardCls} sticky top-4`}>

              {/* Floor filter */}
              <div className={`flex items-center gap-2 px-4 py-3 border-b flex-wrap ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                {floors.map(f => (
                  <button key={String(f.id)}
                    onClick={() => { setSelectedFloor(f.id); setSelectedVerifierId(null); }}
                    className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest transition-all"
                    style={{
                      backgroundColor: selectedFloor === f.id ? tc : (isDark ? 'rgba(255,255,255,0.05)' : '#F3F4F6'),
                      color: selectedFloor === f.id ? '#fff' : (isDark ? '#9CA3AF' : '#6B7280'),
                    }}>
                    {f.label}
                  </button>
                ))}
              </div>

              {/* Verifier search */}
              <div className="px-4 pt-3 pb-2">
                <div className="relative">
                  <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                  <input className={`${inputCls} pl-8 text-xs`} placeholder="Verifier name / Emp ID"
                    value={verifierSearch} onChange={e => setVerifierSearch(e.target.value)} />
                </div>
              </div>

              {/* Verifier list */}
              <div className="overflow-y-auto px-3 pb-2" style={{ maxHeight: 300 }}>
                {verifiersLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin" style={{ color: tc }} />
                  </div>
                ) : filteredVerifiers.length === 0 ? (
                  <p className={`text-center text-[10px] font-black uppercase tracking-widest py-8 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                    No verifier found
                  </p>
                ) : filteredVerifiers.map((v, i) => {
                  const ac   = avatarColors[i % avatarColors.length];
                  const sc   = statusConfig[v.status] || statusConfig.OFFLINE;
                  const isSel = selectedVerifierId === v.id;
                  return (
                    <div key={v.id}
                      onClick={() => setSelectedVerifierId(isSel ? null : v.id)}
                      className="flex items-center gap-2.5 p-2.5 rounded-xl cursor-pointer transition-all border-2 mb-1"
                      style={{
                        borderColor:     isSel ? tc : 'transparent',
                        backgroundColor: isSel ? tc + '1A' : undefined,
                      }}
                    >
                      <CheckBox checked={isSel} onClick={() => setSelectedVerifierId(isSel ? null : v.id)} />
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-black flex-shrink-0"
                        style={{ backgroundColor: ac.bg, color: ac.text }}>
                        {getInitials(v.name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs font-black truncate ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>{v.name}</p>
                        <p className="text-[9px] font-black text-gray-500 uppercase tracking-widest">{v.employeeId}</p>
                      </div>
                      <span className="px-1.5 py-0.5 rounded-full text-[9px] font-black flex-shrink-0"
                        style={{ backgroundColor: sc.bg, color: sc.text }}>
                        {sc.label}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Summary + Assign button */}
              <div className="px-4 pb-4 pt-2 border-t" style={{ borderColor: isDark ? 'rgba(255,255,255,0.05)' : '#F3F4F6' }}>
                {(selectedOrderIds.length > 0 || selectedVerifierId) && (
                  <div className={`mb-3 p-3 rounded-xl border text-[10px] font-black uppercase tracking-widest space-y-1.5 ${isDark ? 'bg-white/5 border-white/5 text-gray-400' : 'bg-gray-50 border-gray-100 text-gray-500'}`}>
                    <p className="flex justify-between">
                      <span>Orders selected</span>
                      <span style={{ color: tc }}>{selectedOrderIds.length}</span>
                    </p>
                    <p className="flex justify-between">
                      <span>Verifier</span>
                      <span style={{ color: tc }}>
                        {selectedVerifierId
                          ? verifiers.find(v => v.id === selectedVerifierId)?.name?.split(' ')[0] || '—'
                          : '—'}
                      </span>
                    </p>
                  </div>
                )}
                <button
                  onClick={handleAssign}
                  disabled={assigning || selectedOrderIds.length === 0 || !selectedVerifierId}
                  className="w-full py-3.5 text-white rounded-2xl transition-all font-black text-[10px] uppercase tracking-widest shadow-xl active:scale-95 disabled:opacity-30 disabled:grayscale disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  style={{ backgroundColor: tc }}
                >
                  {assigning
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Assigning...</>
                    : <><ArrowLeftRight className="w-4 h-4" />
                        Assign {selectedOrderIds.length > 0 ? `(${selectedOrderIds.length})` : ''} Orders
                      </>
                  }
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          RELEASE TAB
      ══════════════════════════════════════════════════════ */}
      {activeTab === 'release' && (
        <div className="max-w-xl">
          <div className={`${cardCls} p-6`}>
            <h3 className={`text-xs font-black uppercase tracking-widest mb-1 ${isDark ? 'text-white' : 'text-gray-800'}`}>
              Release Order
            </h3>
            <p className={`text-xs mb-6 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Remove the manual assign flag — order will go back to auto-assign pool
            </p>
            <div className="space-y-4">
              <div>
                <label className={labelCls}>Order ID</label>
                <input className={inputCls} type="text" placeholder="e.g. OD3379"
                  value={releaseOrderId} onChange={e => setReleaseOrderId(e.target.value)} />
              </div>
              <div className={`p-3 rounded-xl border text-xs font-black ${isDark ? 'bg-yellow-500/10 border-yellow-500/20 text-yellow-400' : 'bg-yellow-50 border-yellow-100 text-yellow-700'}`}>
                ⚠️ After release, the order will return to the auto-assign queue
              </div>
              <button
                onClick={handleRelease}
                disabled={releasing || !releaseOrderId}
                className="w-full py-3.5 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl active:scale-95 disabled:opacity-30 disabled:grayscale disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{ backgroundColor: '#D85A30' }}
              >
                {releasing
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Releasing...</>
                  : <><RotateCcw className="w-4 h-4" /> Release to Pool</>
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Date Range Modal */}
      {showCustomDateModal && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowCustomDateModal(false);
          }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]"
        >
          <div className={`rounded-2xl shadow-2xl w-full max-w-md p-6 border ${isDark ? 'bg-[#111827] border-white/10 text-white' : 'bg-white border-gray-100'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Select Custom Date Range</h3>
              <button
                onClick={() => setShowCustomDateModal(false)}
                className={`p-1.5 rounded-lg transition-colors ${isDark ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-6">
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Start Date – End Date</label>
              <RangePicker
                value={
                  tempStartDate && tempEndDate
                    ? [dayjs(tempStartDate), dayjs(tempEndDate)]
                    : null
                }
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    setTempStartDate(dates[0].format('YYYY-MM-DD'));
                    setTempEndDate(dates[1].format('YYYY-MM-DD'));
                  } else {
                    setTempStartDate('');
                    setTempEndDate('');
                  }
                }}
                format="DD-MM-YYYY"
                style={{ width: '100%', padding: '8px 12px', borderRadius: '10px' }}
                className="w-full text-base"
                size="large"
              />
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button onClick={() => setShowCustomDateModal(false)}
                className={`px-5 py-2 text-sm font-semibold rounded-xl transition-colors ${isDark ? 'bg-white/5 hover:bg-white/10 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}>
                Cancel
              </button>
              <button
                onClick={() => {
                  setDatePreset('custom');
                  setStartDate(tempStartDate);
                  setEndDate(tempEndDate);
                  setCurrentPage(1);
                  setSelectedOrderIds([]);
                  setShowCustomDateModal(false);
                }}
                disabled={!tempStartDate || !tempEndDate}
                className="px-5 py-2 text-sm font-semibold text-white rounded-xl transition-colors hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: tc }}>
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2.5 px-5 py-3 rounded-2xl shadow-2xl text-sm font-black"
          style={{
            backgroundColor: toast.type === 'success' ? '#E1F5EE' : '#FCEBEB',
            color:           toast.type === 'success' ? '#0F6E56' : '#A32D2D',
            border:          `1px solid ${toast.type === 'success' ? '#5DCAA5' : '#F09595'}`,
          }}>
          {toast.type === 'success'
            ? <CheckCircle className="w-4 h-4 flex-shrink-0" />
            : <AlertCircle className="w-4 h-4 flex-shrink-0" />
          }
          {toast.msg}
        </div>
      )}
    </div>
  );
}